import { Box, Button, Typography, useTheme } from '@mui/material';
import Dialog from 'components/dialog';
import Divider from 'components/divider';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import NewListRow from 'components/new-list-row';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import dayjs from 'dayjs';
import { Check, Copy } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { ScenarioCageData } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { ScenarioListItem } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-list-item.type';
import ScenarioInfoGrid from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-info-grid';
import titleCase from 'utils/title-case';

type Props = {
  scenario: ScenarioListItem;
};

const ScenarioListRowBody: React.FC<Props> = ({ scenario }) => {
  const theme = useTheme();
  const snackbarDispatch = useSnackbarDispatch();
  const [copiedCageId, setCopiedCageId] = useState<string | null>(null);

  // #region Columns
  const bodyColumns: HeadCell[] = [
    {
      id: 'fishGroup',
      label: 'Fish group',
    },
    {
      id: 'feedPrograms',
      align: 'left',
      label: 'Feed programs',
      sx: { display: 'flex', justifyContent: 'flex-start', padding: 0 },
    },
    {
      id: 'actions',
      align: 'center',
      label: '',
    },
  ];

  // #endregion

  // #region Helpers
  const fishGroups = useMemo(() => {
    const includedCages = scenario.cageData.filter((cage) => cage.isIncluded);
    return `${includedCages.length} of ${scenario.cageData.length} (${scenario.cageData.length - includedCages.length} excluded) `;
  }, [scenario]);

  const feedPrograms = useMemo(() => {
    const uniquePrograms = new Set(scenario.cageData.filter((cage) => cage.feedProgram).map((cage) => cage.feedProgram.name));
    return `${uniquePrograms.size} feed programs`;
  }, [scenario]);

  const includedCageData = useMemo(() => scenario.cageData.filter((cage) => cage.isIncluded), [scenario]);
  const excludedCageData = useMemo(() => scenario.cageData.filter((cage) => !cage.isIncluded), [scenario]);

  const copyToClipboard = (cageData: ScenarioCageData[], cageId: string) => {
    const feedAndWeights = cageData
      .filter((cage) => cage.isIncluded)
      .map(
        (cage) =>
          `Fish group: ${cage.fishGroupName}, Mean weight: ${cage.meanWeight} g, Feed program: ${cage.feedProgram?.name || 'N/A'}`
      )
      .join('\n');

    navigator.clipboard.writeText(feedAndWeights).then(
      () => {
        setCopiedCageId(cageId);
        setTimeout(() => setCopiedCageId(null), 3000);
      },
      (err) => {
        snackbarDispatch({
          type: SnackbarActionTypes.WARNING_SNACKBAR,
          payload: { msg: err ? String(err) : 'An error occurred' },
        });
      }
    );
  };

  // #endregion

  return (
    <Box display={'flex'} flexDirection={'row'} paddingBottom={6}>
      <Box display={'flex'} flexDirection={'column'} gap={4} padding={2} width={'100%'}>
        <Box display={'flex'} flexDirection={'column'} gap={2} padding={2} width={'35%'}>
          <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'}>
            <Typography variant="h6" sx={{}} fontWeight={'bold'} whiteSpace={'nowrap'}>
              Date Applied:
            </Typography>
            <Typography fontSize={14} whiteSpace={'nowrap'}>
              {scenario.effectiveFrom ? dayjs(scenario.effectiveFrom).utc().format('DD MMM YYYY') : 'N/A'}
            </Typography>
          </Box>
        </Box>

        <List
          dark
          options={bodyColumns}
          sx={{
            ['.MuiTableCell-body']: {
              backgroundColor: `${theme.palette.lightBlue.o25}`,
            },
          }}>
          <NewListRow>
            <ListCell>{fishGroups}</ListCell>
            <ListCell>{feedPrograms}</ListCell>
            <ListCell align="right">
              <Dialog
                maxWidth="lg"
                title={titleCase(scenario.appliedAs)}
                button={
                  <Button size="small" variant="outlined">
                    <FormattedMessage id="general.view" />
                  </Button>
                }>
                <Box display={'flex'} flexDirection={'column'} gap={4}>
                  <ScenarioInfoGrid
                    items={[
                      { label: 'Scenario name', value: scenario.name || 'N/A' },
                      { label: 'Applied as', value: titleCase(scenario.appliedAs) || 'N/A', noWrap: true },
                      {
                        label: 'Date applied',
                        value: scenario.effectiveFrom ? dayjs(scenario.effectiveFrom).utc().format('DD-MM-YYYY') : 'N/A',
                        noWrap: true,
                      },
                      ...(scenario.effectiveTo
                        ? [
                            {
                              label: 'Effective To',
                              value: dayjs(scenario.effectiveTo).utc().format('DD-MM-YYYY'),
                              noWrap: true,
                            },
                          ]
                        : []),
                    ]}
                    dark
                  />

                  <Box display={'flex'} flexDirection={'column'} gap={6}>
                    <List
                      dark
                      sx={{
                        ['.MuiTableCell-body']: {
                          backgroundColor: `${theme.palette.lightBlue.o25}`,
                        },
                      }}
                      options={[
                        { id: 'fishGroup', label: 'Fish group' },
                        { id: 'feedProgram', label: 'Feed program' },
                        { id: 'harvestTarget', label: 'Harvest target' },
                        { id: 'estHarvestDate', label: 'Est. Harvest Date' },
                        { id: 'mortality', label: 'Mortality' },
                      ]}>
                      {includedCageData.map((cage) => (
                        <NewListRow
                          colSpan={8}
                          key={cage.id}
                          body={
                            <>
                              <Divider sx={{ borderColor: theme.palette.darkBlue.o25 }} />
                              <Box
                                display={'flex'}
                                flexDirection={'column'}
                                justifyContent={'space-between'}
                                gap={4}
                                alignItems={'center'}>
                                <List
                                  options={[
                                    { id: 'transferWeights', label: 'Transfer weights' },
                                    { id: 'feeds', label: 'Feeds', align: 'right' },
                                  ]}>
                                  {scenario.cageData.map((cage) => (
                                    <ListRow key={cage.id}>
                                      <ListCell>{cage.meanWeight} g:</ListCell>
                                      <ListCell align="right">{cage.feedProgram?.name}</ListCell>
                                    </ListRow>
                                  ))}
                                </List>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={copiedCageId === cage.id ? <Check /> : <Copy />}
                                  onClick={() => copyToClipboard([cage], cage.id)}
                                  sx={{
                                    mb: 4,
                                    width: '770px',
                                    alignContent: 'center',
                                    transition: 'all 0.3s ease-in-out',
                                    ...(copiedCageId === cage.id && {
                                      color: theme.palette.success.main,
                                    }),
                                  }}>
                                  {copiedCageId === cage.id ? (
                                    'Copied'
                                  ) : (
                                    <>
                                      <FormattedMessage id="general.copy" /> weights and feeds
                                    </>
                                  )}
                                </Button>
                              </Box>
                            </>
                          }>
                          <ListCell>{cage.fishGroupName}</ListCell>
                          <ListCell>{cage.feedProgram?.name}</ListCell>
                          <ListCell>{cage.harvestTarget}</ListCell>
                          <ListCell>
                            {cage.estimatedHarvestDate
                              ? dayjs(cage.estimatedHarvestDate).utc().format('DD MMM YYYY')
                              : 'No est. harvest date'}
                          </ListCell>
                          <ListCell>{(Math.random() * 10).toFixed(2)}%</ListCell>
                        </NewListRow>
                      ))}
                    </List>

                    <List
                      dark
                      sx={{
                        ['.MuiTableCell-body']: {
                          backgroundColor: `${theme.palette.darkBlue.o25}`,
                        },
                      }}
                      options={[
                        { id: 'fishGroup', label: 'Excluded fish groups', sx: { fontWeight: 'normal', fontSize: 16 }, align: 'left' },
                      ]}>
                      {excludedCageData.map((cage) => (
                        <NewListRow colSpan={8} key={cage.id} sx={{ height: '50px' }}>
                          <ListCell>{cage.fishGroupName}</ListCell>
                        </NewListRow>
                      ))}
                    </List>
                  </Box>
                </Box>
              </Dialog>
            </ListCell>
          </NewListRow>
        </List>
      </Box>
    </Box>
  );
};

export default React.memo(ScenarioListRowBody);
